"use client";

import * as React from "react";
import { Badge } from "../primitives/badge";
import { cn } from "../primitives/cn";

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
}

export function AvatarCell({
  name,
  subtitle,
  className,
}: {
  name: string;
  subtitle?: string;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <span
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-[10px] font-semibold text-primary-soft-foreground"
        aria-hidden
      >
        {initialsFromName(name)}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm">{name}</span>
        {subtitle ? (
          <span className="block truncate text-xs text-muted-foreground">
            {subtitle}
          </span>
        ) : null}
      </span>
    </div>
  );
}

const dotColors: Record<
  NonNullable<React.ComponentProps<typeof StatusBadge>["variant"]>,
  string
> = {
  default: "bg-primary",
  secondary: "bg-muted-foreground",
  success: "bg-success",
  warning: "bg-[hsl(var(--warning))]",
  destructive: "bg-destructive",
  accent: "bg-primary",
};

export function StatusBadge({
  label,
  variant = "secondary",
  className,
}: {
  label: string;
  variant?: "default" | "secondary" | "success" | "warning" | "destructive" | "accent";
  className?: string;
}) {
  return (
    <Badge variant={variant === "accent" ? "default" : variant} className={className}>
      <span
        className={cn("mr-1.5 inline-block size-1.5 rounded-full", dotColors[variant])}
        aria-hidden
      />
      {label}
    </Badge>
  );
}

export function PrimarySecondaryCell({
  primary,
  secondary,
  mono,
}: {
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className={cn("truncate text-sm font-medium", mono && "font-mono")}>
        {primary}
      </div>
      {secondary ? (
        <div className="truncate text-xs text-muted-foreground">{secondary}</div>
      ) : null}
    </div>
  );
}
